/**
 * WorkDetail コンポーネント (SC-03)
 *
 * 作品詳細ページ。個別作品の詳細情報・関連イベント・関連作品を表示。
 *
 */

import { useEffect, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/organisms/Header';
import { Footer } from '@/components/organisms/Footer';
import { Badge } from '@/components/atoms/Badge';
import { useLanguageStore } from '@/stores/language-store';
import { useDataStore } from '@/stores/data-store';
import { useI18n } from '@/i18n/useI18n';
import { getLocalizedText } from '@/lib/i18n-helpers';
import { formatDate, formatDuration } from '@/lib/date-formatters';
import { cn } from '@/lib/cn';

/**
 * WorkDetail コンポーネント
 *
 * 使用例:
 * ```tsx
 * <Route path="/works/:id" element={<WorkDetail />} />
 * ```
 */
export function WorkDetail() {
  const { id } = useParams<{ id: string }>();
  const { language } = useLanguageStore();
  const {
    worksLoaded,
    isLoadingWorks,
    worksError,
    fetchWorks,
    findWorkBySlug,
    eventsLoaded,
    eventsError,
    fetchEvents,
    findEventBySlug,
  } = useDataStore();
  const { t } = useI18n();
  const location = useLocation();

  // ページ遷移時にスクロールを最上位にリセット
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // データ読み込み（未ロードかつエラーなしの場合のみ）
  useEffect(() => {
    if (!worksLoaded && !worksError) fetchWorks();
  }, [worksLoaded, worksError, fetchWorks]);

  // slug からO(1)検索
  const work = id ? findWorkBySlug(id) : undefined;

  // 関連イベント表示に必要な場合のみ events を読み込む
  useEffect(() => {
    if (work?.subjectOf?.length && !eventsLoaded && !eventsError) {
      fetchEvents();
    }
  }, [work, eventsLoaded, eventsError, fetchEvents]);

  // 関連イベントを算出
  const relatedEvents = useMemo(() => {
    if (!eventsLoaded) return [];

    return (
      work?.subjectOf?.flatMap((eid) => {
        const e = findEventBySlug(eid);
        return e ? [e] : [];
      }) ?? []
    );
  }, [work, eventsLoaded, findEventBySlug]);

  // 一覧ページへ戻るURLを生成（現在のフィルタ条件を保持）
  const backToListUrl = useMemo(() => {
    if (location.state && typeof location.state === 'object') {
      const from = (location.state as { from?: string }).from;
      if (from) {
        return from;
      }
    }
    return '/works';
  }, [location.state]);

  // ページ状態の判定
  const pageState = worksError
    ? 'error'
    : !worksLoaded || isLoadingWorks
      ? 'loading'
      : !work
        ? 'not-found'
        : 'ready';

  if (pageState !== 'ready') {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main
          id="main-content"
          className="flex flex-1 items-center justify-center"
        >
          <div className="text-center">
            {pageState === 'loading' && (
              <>
                <div className="mb-4 text-4xl">⏳</div>
                <p className="text-muted-foreground">{t('common.loading')}</p>
              </>
            )}
            {pageState === 'error' && (
              <>
                <p className="text-lg font-medium">{t('common.loadError')}</p>
                <button
                  onClick={() => fetchWorks()}
                  className="mt-4 inline-block text-sm text-primary hover:underline"
                >
                  {t('common.retry')}
                </button>
              </>
            )}
            {pageState === 'not-found' && (
              <>
                <div className="mb-4 text-4xl">❌</div>
                <p className="text-lg font-medium">
                  {t('workDetail.notFound')}
                </p>
                <Link
                  to="/works"
                  className="mt-4 inline-block text-sm text-primary hover:underline"
                >
                  {t('common.back')}
                </Link>
              </>
            )}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // pageState === 'ready' guarantees work is defined
  if (!work) return null;

  const title = getLocalizedText(work.title, language);
  const description = getLocalizedText(work.description || '', language);
  const genre = work.genre || '';
  const artform = work.presentation || '';
  const artMedium = work.artMedium || '';
  const filmFormat = work.filmFormat || '';
  const publisher = work.publisher || '';
  const dateCreated = formatDate(work.dateCreated, language);
  const thumbnail = work.image;

  const keywords = work.keywords || [];

  return (
    <div className="flex min-h-screen flex-col">
      <Helmet>
        <title>
          {title} - {t('site.title')}
        </title>
        <meta name="description" content={description || title} />
      </Helmet>

      <Header />

      <main id="main-content" className="flex-1">
        {/* パンくずナビゲーション */}
        <section className="border-b bg-muted/20 py-3">
          <div className="container mx-auto px-4">
            <nav aria-label="Breadcrumb" className="text-sm">
              <ol className="flex items-center gap-2 text-muted-foreground">
                <li>
                  <Link to="/" className="hover:text-foreground">
                    {t('common.home')}
                  </Link>
                </li>
                <li>/</li>
                <li>
                  <Link to="/works" className="hover:text-foreground">
                    {t('worksList.title')}
                  </Link>
                </li>
                <li>/</li>
                <li className="font-medium text-foreground">{title}</li>
              </ol>
            </nav>
          </div>
        </section>

        {/* 一覧へ戻るボタン */}
        <section className="border-b bg-background py-3">
          <div className="container mx-auto px-4">
            <Link
              to={backToListUrl}
              className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z"
                  clipRule="evenodd"
                />
              </svg>
              {t('common.back')}
            </Link>
          </div>
        </section>

        {/* 作品詳細 */}
        <section className="py-8">
          <div className="container mx-auto px-4">
            <div className="grid gap-8 lg:grid-cols-2">
              {/* 左カラム: 画像 */}
              <div
                className={cn(
                  'rounded-lg border shadow-md',
                  !thumbnail && 'flex items-center justify-center bg-muted'
                )}
                data-testid="work-image"
              >
                <img
                  src={thumbnail || '/images/noimage.jpg'}
                  alt={title}
                  className="w-full rounded-lg object-contain"
                  width="640"
                  height="360"
                  onError={(e) => {
                    e.currentTarget.src = '/images/noimage.jpg';
                  }}
                />
              </div>

              {/* 右カラム: 情報 */}
              <div>
                <h1 className="mb-4 text-3xl font-bold">{title}</h1>

                {/* 別タイトル */}
                {work.alternativeHeadline && (
                  <p className="mb-4 text-lg text-muted-foreground">
                    {work.alternativeHeadline}
                  </p>
                )}

                {/* メタ情報 */}
                <div className="mb-6 space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold">
                      {t('workDetail.labels.createdYear')}
                    </span>
                    <span>{dateCreated}</span>
                  </div>

                  {genre ? (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-semibold">
                        {t('workDetail.labels.genre')}
                      </span>
                      <Link
                        to={`/works?genres=${encodeURIComponent(genre)}`}
                        className="inline-block"
                      >
                        <Badge
                          variant="outline"
                          className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                        >
                          {genre}
                        </Badge>
                      </Link>
                    </div>
                  ) : null}

                  {keywords.length > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-semibold">
                        {t('workDetail.labels.keywords')}
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {keywords.map((keyword) => (
                          <Link
                            key={keyword}
                            to={`/works?keywords=${encodeURIComponent(keyword)}`}
                            className="inline-block"
                          >
                            <Badge
                              variant="outline"
                              className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                            >
                              {keyword}
                            </Badge>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* メタデータテーブル */}
                <div
                  className="rounded-lg border overflow-hidden"
                  data-testid="work-metadata-table"
                >
                  <table className="w-full text-sm table-fixed">
                    <tbody>
                      {artform && (
                        <tr className="border-b">
                          <td className="bg-muted/50 px-4 py-2 font-semibold w-1/3">
                            {t('workDetail.labels.artform')}
                          </td>
                          <td className="px-4 py-2 break-words">{artform}</td>
                        </tr>
                      )}
                      {artMedium && (
                        <tr className="border-b">
                          <td className="bg-muted/50 px-4 py-2 font-semibold w-1/3">
                            {t('workDetail.labels.artMedium')}
                          </td>
                          <td className="px-4 py-2 break-words">{artMedium}</td>
                        </tr>
                      )}
                      {filmFormat && (
                        <tr className="border-b">
                          <td className="bg-muted/50 px-4 py-2 font-semibold w-1/3">
                            {t('workDetail.labels.filmFormat')}
                          </td>
                          <td className="px-4 py-2 break-words">
                            {filmFormat}
                          </td>
                        </tr>
                      )}
                      {work.duration && (
                        <tr className="border-b">
                          <td className="bg-muted/50 px-4 py-2 font-semibold w-1/3">
                            {t('workDetail.labels.duration')}
                          </td>
                          <td className="px-4 py-2 break-words">
                            {formatDuration(work.duration, language)}
                          </td>
                        </tr>
                      )}
                      {/* additionalType はUI用JSONに存在しない */}
                      {work.contributor && (
                        <tr className="border-b">
                          <td className="bg-muted/50 px-4 py-2 font-semibold w-1/3">
                            {t('workDetail.labels.staff')}
                          </td>
                          <td className="px-4 py-2 whitespace-pre-wrap break-words">
                            {work.contributor}
                          </td>
                        </tr>
                      )}
                      {publisher && (
                        <tr className="border-b">
                          <td className="bg-muted/50 px-4 py-2 font-semibold w-1/3">
                            {t('workDetail.labels.publisher')}
                          </td>
                          <td className="px-4 py-2 break-words">{publisher}</td>
                        </tr>
                      )}
                      {work.award && (
                        <tr className="border-b">
                          <td className="bg-muted/50 px-4 py-2 font-semibold w-1/3">
                            {t('workDetail.labels.award')}
                          </td>
                          <td className="px-4 py-2 break-words">
                            {work.award}
                          </td>
                        </tr>
                      )}
                      {work.relatedLink && (
                        <tr className="border-b">
                          <td className="bg-muted/50 px-4 py-2 font-semibold w-1/3">
                            {t('workDetail.labels.externalLink')}
                          </td>
                          <td className="px-4 py-2 break-all">
                            <a
                              href={work.relatedLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                              data-testid="work-external-url"
                            >
                              {work.relatedLink}
                            </a>
                          </td>
                        </tr>
                      )}
                      {/* schema:sameAs, dcterms:source, dcterms:rights はUI用JSONに存在しない */}
                      {description && (
                        <tr className="border-b">
                          <td className="bg-muted/50 px-4 py-2 font-semibold w-1/3">
                            {t('workDetail.labels.description')}
                          </td>
                          <td className="px-4 py-2 whitespace-pre-wrap break-words">
                            {description}
                          </td>
                        </tr>
                      )}
                      {work.note && (
                        <tr>
                          <td className="bg-muted/50 px-4 py-2 font-semibold w-1/3">
                            {t('workDetail.labels.notes')}
                          </td>
                          <td className="px-4 py-2 whitespace-pre-wrap break-words">
                            {getLocalizedText(work.note, language)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* 関連イベント */}
                {relatedEvents.length > 0 && (
                  <section
                    aria-labelledby="related-events-heading"
                    className="mt-6 border-t pt-6"
                  >
                    <h2
                      id="related-events-heading"
                      className="mb-4 text-xl font-semibold"
                    >
                      {t('workDetail.relatedEvents')}
                    </h2>
                    <ul className="space-y-2">
                      {relatedEvents.map((event) => (
                        <li key={event.id}>
                          <Link
                            to={`/events/${event.id}`}
                            state={{ from: location.pathname }}
                            className="inline-flex items-baseline gap-3 text-primary hover:underline"
                          >
                            <span>
                              {getLocalizedText(event.title, language)}
                            </span>
                            <span className="text-sm text-muted-foreground">
                              {formatDate(event.startDate, language)}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
